import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Button
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import GroupsIcon from '@mui/icons-material/Groups';
import ChecklistIcon from '@mui/icons-material/Checklist';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LockClockIcon from '@mui/icons-material/LockClock';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

const faqs = [
  {
    question: 'What is a crowd trade?',
    answer: 'A crowd trade is a group trading event where many people bring items, browse what others have, and express what they would be happy to receive. Instead of relying only on one-to-one swaps, CrowdTrades helps coordinate event-based trading so users can join a local event, add items to the shared trade pool, build want lists, and participate in a more organized multi-person trading flow.'
  },
  {
    question: 'How is this different from a normal one-for-one trade?',
    answer: 'Normal trading often stalls when two people do not want each other’s items equally. CrowdTrades is designed around event-based trading with a shared item pool and want-list style preferences. That means the platform can support richer multi-party outcomes, especially during math-trade style events where several people can all get something they want through a coordinated result.'
  },
  {
    question: 'How does CrowdTrades work at a high level?',
    answer: 'You browse nearby events, join one that interests you, add your eligible items into that event’s trade pool, and then review what other attendees brought. From there you can build want lists for items you would be willing to receive. Event owners can run preview math-trade results before cutoff, publish visible previews, and finalize outcomes after cutoff.'
  },
  {
    question: 'How does search work on the home page?',
    answer: 'Home-page search is designed to help you find events quickly without forcing you to open every listing. Search matches against event details and also against item names attached to an event, so you can often find an event by the kinds of things people brought—not just by the event title. It is useful for searching by event names, categories, locations, and item-related keywords.'
  },
  {
    question: 'Can I search by item name, not just event name?',
    answer: 'Yes. CrowdTrades search supports item-aware event discovery. That means if an event has items whose names match your search, the event can still show up even if the event title itself is not an exact match. This is especially helpful when you know what you want to trade for, but not which event contains it.'
  },
  {
    question: 'What other filters help narrow search results?',
    answer: 'In addition to text search, CrowdTrades supports location-aware browsing, search radius filtering, category filtering, and time-based browsing for future, past, or all events depending on the page flow. In practice, the best results usually come from combining a good location with a reasonable radius and a short, specific search phrase.'
  },
  {
    question: 'What is the trade cutoff date?',
    answer: 'The trade cutoff is the last moment when event participation inputs are still open. Before cutoff, users can normally join events, leave events, add items, remove their own items, and manage want lists. After cutoff, the event is locked for trade-input changes so results can be generated fairly from a stable snapshot.'
  },
  {
    question: 'What gets locked after cutoff?',
    answer: 'After cutoff, CrowdTrades now locks the important event mutation paths. Users can no longer join or leave the event, items can no longer be added or removed from the trade pool, and the owner can no longer change the event scheduling details through normal editing. Want-list style math-trade inputs are also treated as locked after cutoff, including wants that use cash offer items.'
  },
  {
    question: 'Can I offer cash in a trade?',
    answer: 'Yes. Cash offers are represented as special cash items in CrowdTrades. You can create a cash item, add it to an event, and use it in want lists like any other event item. Cash offers can be used for normal items, but cash offers cannot target other cash offers.'
  },
  {
    question: 'How do want lists work?',
    answer: 'Want lists let you say what you would be willing to trade for a specific event item. Instead of only marking vague interest, you save an item-based offer that says which of your event items you would trade for that target. This includes cash offer items when allowed by the event rules. Want-list inputs remain editable until the trade cutoff, then they lock along with the rest of the event trade state.'
  },
  {
    question: 'Do I have to trade every item I add?',
    answer: 'No. Adding an item to an event makes it part of that event’s trade pool, but the final outcome still depends on preferences, event rules, and whether a valid result can be found. Users should only add items they are genuinely willing to trade under the rules of that event.'
  },
  {
    question: 'What should I do before joining an event?',
    answer: 'Read the event details carefully. Check the event date, cutoff date, location, category, and the tone of the event. Make sure your items fit the event type, your availability matches the in-person meetup, and you are comfortable with the timing and expectations.'
  },
  {
    question: 'Can I message another trader?',
    answer: 'Yes. Public trader profiles support direct profile-to-profile mailbox messaging. Your own profile also includes your mailbox so you can read messages, track event-related notifications, and see when important actions happened.'
  },
  {
    question: 'How can I track my item activity?',
    answer: 'Your dashboard shows which events each item is listed in, how many offers you have made with it, how many offers you have received, and a more detailed activity view showing active outgoing and incoming offers. This helps you understand where an item is being used before you edit or remove it.'
  },
  {
    question: 'What is the difference between preview results and final trade results?',
    answer: 'Preview results are meant to help event owners and participants understand likely trade outcomes before finalization. Final trade results are based on the locked event state after cutoff and should be treated as the authoritative outcome for that event.'
  },
  {
    question: 'Can I turn off email notifications?',
    answer: 'Yes. Your profile includes email notification checkboxes for system messages, event administrator messages, and messages from other traders. These settings only affect delivery to your personal email account. They do not turn off your CrowdTrades inbox, which still receives all mailbox messages and event-related updates inside the app.'
  },
  {
    question: 'What if an event owner removes my item?',
    answer: 'If an event owner removes another trader’s item from the event, the system requires a reason and sends a mailbox notification. That way people are not left guessing why their item disappeared from the trade pool.'
  },
  {
    question: 'What kind of events fit this site best?',
    answer: 'CrowdTrades is especially useful for hobby communities with lots of collectible or category-focused items—things like tabletop games, books, toys, electronics, and similar community trading events. It works best when users are local enough to actually complete the exchange in person.'
  }
];

const rules = [
  'Only add items you actually own and are willing to trade.',
  'Be honest about item condition, completeness, and important defects.',
  'Use event categories appropriately so people are not browsing junk that does not belong there.',
  'Do not wait until after cutoff expecting to change participation, item pool contents, or event timing.',
  'If you run an event, communicate clearly with participants and use mailbox messaging responsibly.',
  'Treat other users with respect—this is a community trading platform, not a scam playground.',
  'Meet safely in public or otherwise appropriate locations for in-person exchanges.'
];

const tips = [
  {
    title: 'For traders',
    body: 'Use clear photos, accurate descriptions, and realistic expectations. Better item data leads to better decisions and fewer awkward in-person surprises.'
  },
  {
    title: 'For event owners',
    body: 'Set a sensible cutoff, keep the event page updated, and avoid last-minute rule changes. Stable events feel trustworthy.'
  },
  {
    title: 'For new users',
    body: 'Start by browsing nearby events and reading trader profiles. You do not need to understand every advanced math-trade concept on day one to participate.'
  }
];

export default function FaqPage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 4,
            color: 'white',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}
        >
          <Stack spacing={2}>
            <Chip
              icon={<HelpOutlineIcon sx={{ color: 'white !important' }} />}
              label="Help & FAQ"
              sx={{
                width: 'fit-content',
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.18)',
                fontWeight: 700
              }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800 }}>
              CrowdTrades FAQ
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.95, maxWidth: 820, fontWeight: 400 }}>
              What a crowd trade is, how the site works, what locks after cutoff, and the practical rules that keep local trading sane.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/')}
                sx={{
                  backgroundColor: 'white',
                  color: '#5b63d3',
                  fontWeight: 700,
                  '&:hover': { backgroundColor: '#f2f4ff' }
                }}
              >
                Browse Events
              </Button>
              <Button
                variant="outlined"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/event-day-guide')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.6)',
                  color: 'white',
                  fontWeight: 700,
                  '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' }
                }}
              >
                Event-Day Guide
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <SwapHorizIcon sx={{ color: '#667eea', fontSize: 34 }} />
                  <Typography variant="h6">Shared trade pool</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Events are built around multiple traders adding items into one local pool instead of relying only on one-off direct swaps.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <GroupsIcon sx={{ color: '#667eea', fontSize: 34 }} />
                  <Typography variant="h6">Community-first</Typography>
                  <Typography variant="body2" color="text.secondary">
                    CrowdTrades works best for local communities that actually intend to meet up and exchange items in person.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <ChecklistIcon sx={{ color: '#667eea', fontSize: 34 }} />
                  <Typography variant="h6">Want lists</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Instead of vague interest, users can build structured preferences that support more organized trading outcomes.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ height: '100%', borderRadius: 4 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <LockClockIcon sx={{ color: '#667eea', fontSize: 34 }} />
                  <Typography variant="h6">Cutoff locks</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Once cutoff passes, the platform locks inputs so the trade state cannot keep shifting underneath participants.
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5 }}>
                Frequently asked questions
              </Typography>
              <Stack spacing={1.5}>
                {faqs.map((faq) => (
                  <Accordion key={faq.question} disableGutters sx={{ borderRadius: '16px !important', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 600 }}>{faq.question}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body1" sx={{ color: '#4f5b67', lineHeight: 1.75 }}>
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <ShieldOutlinedIcon sx={{ color: '#667eea' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Site rules & expectations
                  </Typography>
                </Stack>
                <List disablePadding>
                  {rules.map((rule, index) => (
                    <ListItem key={rule} disableGutters sx={{ alignItems: 'flex-start', py: 0.75 }}>
                      <ListItemIcon sx={{ minWidth: 30, mt: 0.25 }}>
                        <Chip label={index + 1} size="small" sx={{ fontWeight: 700, backgroundColor: '#eef1ff', color: '#5b63d3' }} />
                      </ListItemIcon>
                      <ListItemText primary={rule} primaryTypographyProps={{ sx: { lineHeight: 1.6 } }} />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <EventAvailableIcon sx={{ color: '#667eea' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Typical event flow
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {[
                    'Browse nearby events and choose one that matches your interests.',
                    'Join the event before cutoff and add items you are truly willing to trade.',
                    'Browse other attendees’ items, build your want lists, and create a cash offer item if you want to use cash in a trade.',
                    'Use your dashboard and item activity views to keep track of where your items are listed and what offers are active.',
                    'Wait for owner previews, published previews, or final outcomes depending on the event format.',
                    'Meet up and complete the exchange in the real world.'
                  ].map((step, index) => (
                    <Box key={step} sx={{ display: 'flex', gap: 1.5 }}>
                      <Chip label={index + 1} size="small" sx={{ mt: 0.2, fontWeight: 700, backgroundColor: '#eef1ff', color: '#5b63d3' }} />
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{step}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>

              <Paper sx={{ p: 3, borderRadius: 4 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                  <MailOutlineIcon sx={{ color: '#667eea' }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Helpful tips
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  {tips.map((tip, index) => (
                    <React.Fragment key={tip.title}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                          {tip.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                          {tip.body}
                        </Typography>
                      </Box>
                      {index < tips.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </Stack>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
